
import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

interface AuthGuardProps {
  children: React.ReactNode;
}

const AuthGuard = ({ children }: AuthGuardProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const location = useLocation();
  const { toast } = useToast();

  useEffect(() => {
    // Check if user is authenticated
    const authData = localStorage.getItem("7gc_auth");
    if (!authData) {
      setIsAuthenticated(false);
      return;
    }

    try {
      const auth = JSON.parse(authData);
      // Check if token is valid and not expired (12 hour session)
      const isValid = auth.isAuthorized === true;
      const isExpired = new Date().getTime() - auth.timestamp > 12 * 60 * 60 * 1000;
      
      if (isValid && !isExpired) {
        setIsAuthenticated(true);
      } else {
        // Clear invalid auth data
        localStorage.removeItem("7gc_auth");
        setIsAuthenticated(false);
      }
    } catch (error) {
      // Handle corrupted auth data
      localStorage.removeItem("7gc_auth");
      setIsAuthenticated(false);
    }
  }, []);

  // Show loading state while checking authentication
  if (isAuthenticated === null) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-black">
        <div className="text-primary text-xl animate-pulse">Provjera autentikacije...</div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    toast({
      title: "Pristup odbijen",
      description: "Molimo prijavite se za pristup ovoj stranici",
      variant: "destructive",
    });
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // User is authenticated, render the protected content
  return <>{children}</>;
};

export default AuthGuard;
